import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as CrmActions from '../../../../state/crm/crm.actions';
import * as CrmSelectors from '../../../../state/crm/crm.selectors';

export interface DealDetail {
  id: string;
  name: string;
  deal_value: number;
  company_id: string;
  contact_id: string;
  icp_score?: number;
  lead_source?: string;
  user_id: string;
  deal_stage: 'Early' | 'Progress' | 'Closing' | 'Won / Renewal' | 'Lost' | 'Archived';
  workspace_id: string;
  close_date: string;
  type: 'Service' | 'Product' | 'N/A';
  formatted_deal_value?: string;
  created_at: string;
  updated_at: string;
  company?: {
    id: string;
    name: string;
  };
  contact?: {
    id: string;
    name: string;
    surname: string;
  };
  owner?: {
    id: string;
    name: string;
  };
  workspace?: {
    id: string;
    name: string;
  };
}

@Component({
  selector: 'app-view-deal-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './view-deal-modal.component.html',
  styleUrl: './view-deal-modal.component.scss'
})
export class ViewDealModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible: boolean = false;
  @Output() close = new EventEmitter<void>();

  deal$: Observable<DealDetail | null>;
  loading$: Observable<boolean>;
  error$: Observable<string | null>;

  private destroy$ = new Subject<void>();

  constructor(private store: Store) {
    this.deal$ = this.store.select(CrmSelectors.selectDealDetail);
    this.loading$ = this.store.select(CrmSelectors.selectDealDetailLoading);
    this.error$ = this.store.select(CrmSelectors.selectDealDetailError);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // No need to do anything here since the data is managed by the parent component
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void {
    this.close.emit();
  }

  getInitials(name: string): string {
    const words = name?.split(' ') || [];
    return words.slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('');
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCloseDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  getDealStageClass(stage: string): string {
    switch (stage?.toLowerCase()) {
      case 'early':
        return 'bg-gray-100 text-gray-800';
      case 'progress':
        return 'bg-blue-100 text-blue-800';
      case 'closing':
        return 'bg-yellow-100 text-yellow-800';
      case 'won / renewal':
        return 'bg-green-100 text-green-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getDealTypeClass(type: string): string {
    switch (type?.toLowerCase()) {
      case 'service':
        return 'bg-purple-100 text-purple-800';
      case 'product':
        return 'bg-indigo-100 text-indigo-800';
      case 'n/a':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getContactFullName(contact: any): string {
    if (!contact) return 'No contact assigned';
    const firstName = contact.name || '';
    const lastName = contact.surname || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Contact';
  }
}